import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl, type MessageDescriptor } from 'react-intl';
import type { SerializedProfileAsset } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { DetailField } from '../../studio/details/DetailField';
import { useGetProfileAssetVersionsQuery, useSetProfileAssetCurrentMutation } from '../../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../../redux/api/utils/isSuccessfulResult';
import { StudioActionError } from '../../studio/StudioActionError';
import { studioActionErrorFeedback, type StudioActionFeedback } from '../../studio/studioActionErrorText';
import { kindHasPlainCover, profileFileUrl, sourceLabel } from '../profileAssets';
import { profileMessages as m } from '../profileMessages';
import { ProfileChipBadge } from '../page/ProfileChipBadge';
import type { ProfileChipTone } from '../profileChips';
import { versionRows, type VersionChip } from './profileVersionRows';
import { profileToolMessages as t } from './profileToolMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

const CHIPS: Readonly<Record<VersionChip, { tone: ProfileChipTone; text: MessageDescriptor }>> = {
  current: { tone: 'ok', text: t.chipCurrent },
  older: { tone: 'nil', text: t.chipOlder },
  noFile: { tone: 'nil', text: t.chipNoFile },
  making: { tone: 'amber', text: m.chipMaking },
  failed: { tone: 'bad', text: m.chipFailed },
};

interface ProfileVersionsProps {
  accountId: number;
  /** A version of the kind and variant (the current one, else the newest); null: none yet. */
  asset: SerializedProfileAsset | null;
}

/**
 * "Versions" of a tool (design b1): the versions of the kind, the newest first. "Use this one" makes
 * an older ready version current again (`POST .../assets/:uuid/current`). The last 3 keep their files.
 */
const ProfileVersionsComponent: React.FC<ProfileVersionsProps> = ({ accountId, asset }) => {
  const intl = useIntl();
  const { data } = useGetProfileAssetVersionsQuery(
    { accountId, assetUuid: asset?.uuid ?? '' },
    { skip: asset === null }
  );
  const [setCurrent, { isLoading }] = useSetProfileAssetCurrentMutation();
  const [feedback, setFeedback] = useState<StudioActionFeedback | null>(null);
  const pickVersion = (assetUuid: string) => {
    setFeedback(null);
    setCurrent({ accountId, assetUuid })
      .then((result) => {
        if (!isSuccessfulResult(result)) {
          setFeedback(studioActionErrorFeedback(intl, 'profile current', result.error));
        }
      })
      .catch(console.error);
  };
  const rows = useMemo(() => versionRows(data?.versions ?? []), [data]);

  const lineOf = (version: SerializedProfileAsset): string => {
    const date = intl.formatDate(version.createdAt, { day: 'numeric', month: 'short' });
    const source = intl.formatMessage(sourceLabel(version.source, kindHasPlainCover(version)));
    const named = version.catalogItemTitle === null ? source : `${source} · ${version.catalogItemTitle}`;
    const line =
      version.createdBy === null
        ? intl.formatMessage(t.versionMade, { date, source: named })
        : intl.formatMessage(t.versionMadeBy, { date, name: version.createdBy.email, source: named });
    return version.fileRemoved ? intl.formatMessage(t.versionFileRemoved, { line }) : line;
  };

  return (
    <DetailField title={intl.formatMessage(t.versionsTitle)} hint={intl.formatMessage(t.versionsHint)}>
      {rows.length === 0 && <span className={styles.meta}>{intl.formatMessage(t.versionsEmpty)}</span>}
      <div className={styles.versions} data-testid="profile-versions">
        {rows.map((row) => {
          const url = profileFileUrl(row.asset);
          return (
            <div
              key={row.asset.uuid}
              className={clsx(styles.version, row.chip === 'current' && styles.versionCurrent)}
              data-testid={`profile-version-${row.asset.version}`}
            >
              <span
                className={styles.versionThumb}
                style={url === null ? undefined : { backgroundImage: `url("${url}")` }}
              />
              <div className={styles.grow}>
                <b>
                  {intl.formatMessage(row.chip === 'current' ? t.versionCurrent : t.versionOther, {
                    version: row.asset.version,
                  })}
                </b>
                <small data-clarity-mask="true">{lineOf(row.asset)}</small>
              </div>
              <ProfileChipBadge chip={CHIPS[row.chip]} />
              {row.canUse && (
                <SMLButton
                  className={styles.sm}
                  disabled={isLoading}
                  onClick={() => {
                    pickVersion(row.asset.uuid);
                  }}
                >
                  {intl.formatMessage(t.useThisOne)}
                </SMLButton>
              )}
            </div>
          );
        })}
      </div>
      <StudioActionError feedback={feedback} />
    </DetailField>
  );
};

export const ProfileVersions = TypedMemo(ProfileVersionsComponent);
